import React, { useState, useCallback, useMemo } from 'react';
import moment from 'moment';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export const Calendar = ({
  selectedStartDate,
  selectedEndDate,
  onDateRangeChange,
  events
}) => {
  const [currentMonth, setCurrentMonth] = useState(moment());
  const [isSelecting, setIsSelecting] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(null);

  const getEventsForDate = useCallback((date) => {
    if (selectedStartDate && selectedEndDate) {
      if (!date.isBetween(selectedStartDate, selectedEndDate, 'day', '[]')) {
        return [];
      }
    }
    
    return events.filter(eventData => {
      const event = eventData.event;
      
      if (eventData.type === 2) {
        // Task - check if date matches start, end, or falls within range
        const taskStart = event.startDate ? moment(event.startDate) : null;
        const taskEnd = event.endDate ? moment(event.endDate) : null;
        
        if (!taskStart && !taskEnd) return false;
        
        // Only start date - show on start date
        if (taskStart && !taskEnd) {
          return date.isSame(taskStart, 'day');
        }
        
        // Only end date - show on end date
        if (!taskStart && taskEnd) {
          return date.isSame(taskEnd, 'day');
        }
        
        // Both dates - show if date is same as start, end, or between them
        return date.isSame(taskStart, 'day') || 
               date.isSame(taskEnd, 'day') || 
               date.isBetween(taskStart, taskEnd, 'day', '()');
      } else if (eventData.type === 3 && event.dateImpact) {
        // Risk
        return date.isSame(moment(event.dateImpact), 'day');
      } else if (eventData.type === 4 && event.startTime) {
        // Meeting
        return date.isSame(moment(event.startTime), 'day');
      } else if (eventData.type === 5 && event.startTime) {
        // Communication Plan
        return date.isSame(moment(event.startTime), 'day');
      }
      
      return false;
    });
  }, [events]);

  const handleDateClick = useCallback((date) => {
    if (!isSelecting) {
      setIsSelecting(true);
      setTempStartDate(date);
    } else {
      if (tempStartDate) {
        const start = tempStartDate.isBefore(date) ? tempStartDate : date;
        const end = tempStartDate.isBefore(date) ? date : tempStartDate;
        onDateRangeChange(start, end);
      }
      setIsSelecting(false);
      setTempStartDate(null);
    }
  }, [isSelecting, tempStartDate, onDateRangeChange]);

  const calendarDays = useMemo(() => {
    const startOfMonth = currentMonth.clone().startOf('month');
    const endOfMonth = currentMonth.clone().endOf('month');
    const startOfCalendar = startOfMonth.clone().startOf('week');
    const endOfCalendar = endOfMonth.clone().endOf('week');

    const days = [];
    let current = startOfCalendar.clone();

    while (current.isSameOrBefore(endOfCalendar)) {
      days.push({
        date: current.clone(),
        isCurrentMonth: current.isSame(currentMonth, 'month'),
        isToday: current.isSame(moment(), 'day'),
        isSelected: selectedStartDate && selectedEndDate && 
                   current.isBetween(selectedStartDate, selectedEndDate, 'day', '[]'),
        isRangeStart: selectedStartDate && current.isSame(selectedStartDate, 'day'),
        isRangeEnd: selectedEndDate && current.isSame(selectedEndDate, 'day'),
        isTempSelected: isSelecting && tempStartDate && 
                       current.isBetween(tempStartDate, moment(), 'day', '[]'),
        hasEvents: getEventsForDate(current).length > 0,
        eventCount: getEventsForDate(current).length
      });
      current.add(1, 'day');
    }

    return days;
  }, [currentMonth, selectedStartDate, selectedEndDate, isSelecting, tempStartDate, getEventsForDate]);

  return (
    <div className="calendar">
      <div className="calendar__header">
        <button
          className="calendar__nav-button"
          onClick={() => setCurrentMonth(prev => prev.clone().subtract(1, 'month'))}
        >
          <ChevronLeft />
        </button>
        <h4 className="calendar__month-year">
          {currentMonth.format('MMMM YYYY')}
        </h4>
        <button
          className="calendar__nav-button"
          onClick={() => setCurrentMonth(prev => prev.clone().add(1, 'month'))}
        >
          <ChevronRight />
        </button>
      </div>

      <div className="calendar__weekdays">
        {moment.weekdaysShort(true).map((day, index) => (
          <div key={index} className="calendar__weekday">
            {day}
          </div>
        ))}
      </div>

      <div className="calendar__grid">
        {calendarDays.map((day, index) => (
          <button
            key={index}
            className={`calendar__day ${
              !day.isCurrentMonth ? 'calendar__day--disabled' : ''
            } ${
              day.isToday ? 'calendar__day--today' : ''
            } ${
              day.isSelected ? 'calendar__day--selected' : ''
            } ${
              day.isRangeStart ? 'calendar__day--range-start' : ''
            } ${
              day.isRangeEnd ? 'calendar__day--range-end' : ''
            } ${
              day.isTempSelected ? 'calendar__day--temp-selected' : ''
            } ${
              day.hasEvents ? 'calendar__day--has-events' : ''
            }`}
            onClick={() => handleDateClick(day.date)}
            disabled={!day.isCurrentMonth}
          >
            <span className="calendar__day-number">{day.date.format('D')}</span>
            {day.hasEvents && (
              <div className="calendar__event-indicator">
                <span className="calendar__event-count">{day.eventCount}</span>
              </div>
            )}
          </button>
        ))}
      </div>

      {isSelecting && (
        <div className="calendar__selection-hint">
          Click another date to complete your selection
        </div>
      )}
    </div>
  );
};